/**
 * Connector Interaction Detail Page
 * Full-page view: metadata, pipeline status, transcript, audio
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Phone, MessageSquare, Mail, User, Clock, Hash,
  RotateCw, Loader2, CheckCircle, XCircle, AlertCircle,
} from 'lucide-react';
import MetricCard from '../components/dashboard/MetricCard';
import Spinner from '../components/shared/Spinner';
import RefreshButton from '../components/shared/RefreshButton';
import {
  getConnector,
  getInteractionDetail,
  retryInteractionProcessing,
  getInteractionRecording,
} from '../services/connectorService';
import { getPipelineStatus, retryPipelineStep, getInteractionTranscript } from '../services/analyticsService';
import { getAnalysesList } from '../services/sentimentService';
import { PermissionGate, usePermissions } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import { getQAScore } from '../services/qaService';
import { agentLabel, typeLabel } from '../utils/formatters';

// Pipeline step display config
const STEP_CONFIG = {
  sentiment: { label: 'Sentiment Analysis', icon: '💬' },
  qa: { label: 'QA Report', icon: '📋' },
  escalation: { label: 'Escalation Prediction', icon: '🔔' },
  location: { label: 'Location Extraction', icon: '📍' },
};

const STEP_STATUS_STYLES = {
  completed: { bg: 'bg-green-50 border-green-200', text: 'text-green-700', icon: CheckCircle },
  pending: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', icon: Clock },
  failed: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', icon: XCircle },
  processing: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', icon: Loader2 },
};

const TYPE_ICONS = { call: Phone, chat: MessageSquare, email: Mail };

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  if (seconds === 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

/* ---------- Pipeline Step Card ---------- */

const PipelineStepCard = ({ step, onRetry, retrying }) => {
  const config = STEP_CONFIG[step.step_type] || { label: step.step_type, icon: '⚙️' };
  const style = STEP_STATUS_STYLES[step.status] || STEP_STATUS_STYLES.pending;
  const StatusIcon = style.icon;

  return (
    <div className={`rounded-lg border p-4 ${style.bg}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <span className="text-base" role="img" aria-hidden="true">{config.icon}</span>
          <span className="text-sm font-medium text-gray-900">{config.label}</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <StatusIcon size={14} className={`${style.text} ${step.status === 'processing' ? 'animate-spin' : ''}`} />
          <span className={`text-xs font-medium capitalize ${style.text}`}>{step.status}</span>
        </div>
      </div>

      {step.retry_count > 0 && (
        <p className="text-[10px] text-gray-500 mb-1">Retry attempts: {step.retry_count}/{step.max_retries}</p>
      )}

      {step.error && (
        <div className="flex items-start space-x-1.5 mt-1 mb-2">
          <AlertCircle size={12} className="text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-[10px] text-red-600 break-words">{step.error}</p>
        </div>
      )}

      {/* onRetry is withheld by the parent when the caller lacks the permission,
          so the button only exists when the action is allowed. */}
      {step.status === 'failed' && onRetry && (
        <button
          onClick={() => onRetry(step.step_type)}
          disabled={retrying}
          className="mt-2 flex items-center space-x-1.5 text-xs text-red-700 hover:text-red-900 font-medium disabled:opacity-50"
        >
          {retrying ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
          <span>{retrying ? 'Retrying...' : 'Retry'}</span>
        </button>
      )}
    </div>
  );
};

/* ---------- Main Page ---------- */

const ConnectorInteractionDetailPage = () => {
  const { connectorId, interactionId } = useParams();
  const navigate = useNavigate();

  // Pipeline-step retries go to analytics_service; whole-interaction and
  // transcript regeneration go to connector_service. Different permissions.
  const { hasPermission } = usePermissions();
  const canRetryStep = hasPermission(StandardPermission.Analytics.REPORTS);

  const [connector, setConnector] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pipelineSteps, setPipelineSteps] = useState([]);
  const [retryingStep, setRetryingStep] = useState(null);
  const [retryingProcessing, setRetryingProcessing] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcriptMessages, setTranscriptMessages] = useState([]);
  const [sentimentData, setSentimentData] = useState(null);
  const [qaData, setQaData] = useState(null);
  const [regeneratingTranscript, setRegeneratingTranscript] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [connRes, detailRes] = await Promise.all([
        getConnector(connectorId),
        getInteractionDetail(connectorId, interactionId),
      ]);

      if (connRes.success) setConnector(connRes.data);
      else toast.error(connRes.error || 'Failed to load connector info');
      if (detailRes.success) {
        setDetail(detailRes.data);
        // Fetch pipeline status if transcription completed
        if (detailRes.data?.processing_status === 'completed') {
          const [pRes, tRes, sRes, qRes] = await Promise.all([
            getPipelineStatus(detailRes.data.id),
            getInteractionTranscript(detailRes.data.id),
            getAnalysesList({ interaction_id: String(detailRes.data.id), page_size: 1 }),
            getQAScore(String(detailRes.data.id)),
          ]);
          if (pRes.success && pRes.data?.steps) setPipelineSteps(pRes.data.steps);
          if (tRes.success && tRes.data?.transcript_data?.messages) {
            setTranscriptMessages(tRes.data.transcript_data.messages);
          }
          if (sRes.success && sRes.data?.analyses?.length > 0) {
            setSentimentData(sRes.data.analyses[0]);
          }
          if (qRes.success && qRes.data) {
            setQaData(qRes.data);
          }
        }
      } else {
        toast.error(detailRes.error);
      }
    } catch {
      toast.error('Failed to load interaction detail');
    } finally {
      setLoading(false);
    }
  }, [connectorId, interactionId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch audio as blob URL (with unmount guard to prevent leak)
  useEffect(() => {
    if (!detail?.audio_file_path) return;
    let mounted = true;
    let objectUrl = null;
    const loadAudio = async () => {
      const res = await getInteractionRecording(connectorId, interactionId);
      if (!mounted) return;
      if (res.success) {
        objectUrl = URL.createObjectURL(res.data);
        setAudioUrl(objectUrl);
      } else {
        toast.error('Failed to load recording');
      }
    };
    loadAudio();
    return () => {
      mounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [connectorId, interactionId, detail?.audio_file_path]);

  const handleRetryStep = async (stepType) => {
    if (!detail) return;
    setRetryingStep(stepType);
    const res = await retryPipelineStep(detail.id, stepType);
    if (res.success) {
      toast.success(`${STEP_CONFIG[stepType]?.label || stepType} retry queued`);
      const pRes = await getPipelineStatus(detail.id);
      if (pRes.success && pRes.data?.steps) setPipelineSteps(pRes.data.steps);
    } else {
      toast.error(res.error);
    }
    setRetryingStep(null);
  };

  const handleRetryProcessing = async () => {
    setRetryingProcessing(true);
    const res = await retryInteractionProcessing(connectorId, interactionId);
    if (res.success) {
      toast.success(res.data.message);
      fetchData();
    } else {
      toast.error(res.error);
    }
    setRetryingProcessing(false);
  };

  const handleRegenerateTranscript = async () => {
    setRegeneratingTranscript(true);
    const res = await retryInteractionProcessing(connectorId, interactionId);
    if (res.success) {
      toast.success('Transcription regeneration started');
      fetchData();
    } else {
      toast.error(res.error);
    }
    setRegeneratingTranscript(false);
  };

  if (loading) return <Spinner />;
  if (!detail) return null;

  const TypeIcon = TYPE_ICONS[detail.interaction_type] || Phone;
  const isFailed = detail.processing_status === 'audio_failed' || detail.processing_status === 'transcription_failed';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(`/connectors/${connectorId}/interactions`)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Back to interactions"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Interaction — {detail.source_id || interactionId}
            </h2>
            <p className="text-xs text-gray-500">
              {connector?.display_name || 'Connector'} &middot; {formatDate(detail.occurred_at)}
            </p>
          </div>
        </div>
        <RefreshButton onClick={fetchData} loading={loading} />
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard
          title="Type"
          value={detail.interaction_type || '—'}
          icon={TypeIcon}
        />
        <MetricCard
          title="Processing"
          value={detail.processing_status?.replace(/_/g, ' ') || '—'}
        />
        <MetricCard
          title="Duration"
          value={formatDuration(detail.duration_seconds)}
          icon={Clock}
        />
        <MetricCard
          title="Confidence"
          value={detail.transcript_confidence != null ? `${(detail.transcript_confidence * 100).toFixed(1)}%` : '—'}
        />
      </div>

      {/* Metadata */}
      <div className="card p-5 mb-6">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Details</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <span className="text-[10px] text-gray-400 uppercase block">Customer</span>
            <p className="text-sm text-gray-900 flex items-center space-x-1">
              <User size={12} className="text-gray-400" />
              <span>{detail.customer_identifier || '—'}</span>
            </p>
          </div>
          <div>
            <span className="text-[10px] text-gray-400 uppercase block">Agent</span>
            <p className="text-sm text-gray-900 flex items-center space-x-1">
              <User size={12} className="text-gray-400" />
              <span title={detail.agent_identifier || ''}>{agentLabel(detail)}</span>
            </p>
          </div>
          <div>
            <span className="text-[10px] text-gray-400 uppercase block">Source ID</span>
            <p className="text-sm text-gray-900 font-mono flex items-center space-x-1">
              <Hash size={12} className="text-gray-400" />
              <span>{detail.source_id || '—'}</span>
            </p>
          </div>
          <div>
            <span className="text-[10px] text-gray-400 uppercase block">Source Type</span>
            <p className="text-sm text-gray-900 capitalize">{typeLabel(detail.source_type) || '—'}</p>
          </div>
          <div>
            <span className="text-[10px] text-gray-400 uppercase block">Call Status</span>
            <p className="text-sm text-gray-900 capitalize">{detail.status || '—'}</p>
          </div>
        </div>
      </div>

      {/* Analytics Pipeline */}
      {pipelineSteps.length > 0 && (
        <div className="card p-5 mb-6">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Analytics Pipeline</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {pipelineSteps.map((step) => (
              <PipelineStepCard
                key={step.step_type}
                step={step}
                onRetry={canRetryStep ? handleRetryStep : undefined}
                retrying={retryingStep === step.step_type}
              />
            ))}
          </div>
        </div>
      )}

      {/* Retry Processing (for failed audio/transcription) */}
      {isFailed && (
        <div className="card p-5 mb-6 border-red-200 bg-red-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-red-800">Processing Failed</h3>
              <p className="text-xs text-red-600 mt-1">
                {detail.processing_status === 'audio_failed'
                  ? 'Audio download failed. Check the recording URL and try again.'
                  : 'Transcription failed. The audio may be corrupted or too long.'}
              </p>
            </div>
            {/* connector_service — analytics:connectors */}
            <PermissionGate permission={StandardPermission.Analytics.CONNECTORS}>
              <button
                onClick={handleRetryProcessing}
                disabled={retryingProcessing}
                className="flex items-center space-x-1.5 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg px-4 py-2 disabled:opacity-50 transition-colors"
              >
                {retryingProcessing ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
                <span>{retryingProcessing ? 'Retrying...' : 'Retry Processing'}</span>
              </button>
            </PermissionGate>
          </div>
        </div>
      )}

      {/* Sentiment Report */}
      {sentimentData && (
        <div className="card p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sentiment Report</h3>
            {/* analytics_service — analytics:reports. Same label as the
                transcript Regenerate further down, different service. */}
            <PermissionGate permission={StandardPermission.Analytics.REPORTS}>
              <button
                onClick={() => handleRetryStep('sentiment')}
                disabled={retryingStep === 'sentiment'}
                className="flex items-center space-x-1 text-[11px] text-gray-500 hover:text-primary-600 transition-colors disabled:opacity-50"
              >
                {retryingStep === 'sentiment' ? <Loader2 size={10} className="animate-spin" /> : <RotateCw size={10} />}
                <span>Regenerate</span>
              </button>
            </PermissionGate>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <span className="text-[10px] text-gray-400 uppercase block">Overall</span>
              <span className={`text-sm font-semibold capitalize ${
                sentimentData.overall_sentiment === 'positive' || sentimentData.overall_sentiment === 'very_positive' ? 'text-green-600'
                : sentimentData.overall_sentiment === 'negative' || sentimentData.overall_sentiment === 'very_negative' ? 'text-red-600'
                : 'text-gray-600'
              }`}>
                {(sentimentData.overall_sentiment || '—').replace(/_/g, ' ')}
              </span>
            </div>
            <div>
              <span className="text-[10px] text-gray-400 uppercase block">Score</span>
              <p className="text-sm font-semibold text-gray-900">
                {sentimentData.sentiment_score != null ? `${Math.round(sentimentData.sentiment_score * 100)}%` : '—'}
              </p>
            </div>
            <div>
              <span className="text-[10px] text-gray-400 uppercase block">Contact Reason</span>
              <p className="text-sm text-gray-700">{sentimentData.contact_reason || '—'}</p>
            </div>
            <div>
              <span className="text-[10px] text-gray-400 uppercase block">Disposition</span>
              <p className="text-sm text-gray-700 capitalize">{sentimentData.disposition || '—'}</p>
            </div>
          </div>
          {sentimentData.summary && (
            <div>
              <span className="text-[10px] text-gray-400 uppercase block mb-1">Summary</span>
              <p className="text-sm text-gray-700 leading-relaxed">{sentimentData.summary}</p>
            </div>
          )}
          {sentimentData.key_emotions && sentimentData.key_emotions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {sentimentData.key_emotions.map((emotion, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                  {emotion}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* QA Report */}
      {qaData && (
        <div className="card p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">QA Report</h3>
            {/* analytics_service — analytics:reports */}
            <PermissionGate permission={StandardPermission.Analytics.REPORTS}>
              <button
                onClick={() => handleRetryStep('qa')}
                disabled={retryingStep === 'qa'}
                className="flex items-center space-x-1 text-[11px] text-gray-500 hover:text-primary-600 transition-colors disabled:opacity-50"
              >
                {retryingStep === 'qa' ? <Loader2 size={10} className="animate-spin" /> : <RotateCw size={10} />}
                <span>Regenerate</span>
              </button>
            </PermissionGate>
          </div>
          {/* Hero score */}
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-lg font-bold border-4 ${
              qaData.total_score_100 >= 80 ? 'border-green-400 text-green-700 bg-green-50'
              : qaData.total_score_100 >= 60 ? 'border-amber-400 text-amber-700 bg-amber-50'
              : 'border-red-400 text-red-700 bg-red-50'
            }`}>
              {qaData.total_score_100 != null ? Math.round(qaData.total_score_100) : '—'}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{qaData.quality_category || '—'}</p>
              {qaData.is_auto_fail && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Auto-Fail</span>
              )}
            </div>
          </div>
          {/* Fatal error banner */}
          {qaData.is_auto_fail && qaData.fatal_errors?.length > 0 && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 mb-4">
              <p className="text-xs font-semibold text-red-700 mb-1">Fatal Error — Auto-Fail</p>
              {qaData.fatal_errors.map((fe, i) => (
                <p key={i} className="text-xs text-red-600">
                  <span className="font-medium">{fe.type}:</span> {fe.description}
                </p>
              ))}
            </div>
          )}
          {/* Sub-parameter scores */}
          {qaData.sub_parameter_scores && qaData.sub_parameter_scores.length > 0 && (
            <div className="space-y-2 mb-4">
              {qaData.sub_parameter_scores.map((param, i) => {
                const pct = param.max_score > 0 ? (param.score / param.max_score) * 100 : 0;
                return (
                  <div key={param.parameter_id || i} className="flex items-center gap-3">
                    <span className="text-xs text-gray-600 w-40 flex-shrink-0 truncate" title={param.name}>
                      {param.name || `Param ${param.parameter_id}`}
                    </span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-14 text-right flex-shrink-0">
                      {param.score}/{param.max_score}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {/* Strengths & Improvements */}
          {(qaData.strengths?.length > 0 || qaData.improvements?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {qaData.strengths?.length > 0 && (
                <div className="p-3 rounded-lg bg-green-50 border border-green-100">
                  <p className="text-[10px] font-semibold text-green-700 uppercase mb-1">Strengths</p>
                  <ul className="space-y-0.5">
                    {qaData.strengths.map((s, i) => (
                      <li key={i} className="text-xs text-green-800 flex items-start gap-1">
                        <span className="mt-1 flex-shrink-0">•</span><span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {qaData.improvements?.length > 0 && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                  <p className="text-[10px] font-semibold text-amber-700 uppercase mb-1">Improvements</p>
                  <ul className="space-y-0.5">
                    {qaData.improvements.map((s, i) => (
                      <li key={i} className="text-xs text-amber-800 flex items-start gap-1">
                        <span className="mt-1 flex-shrink-0">•</span><span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {/* Coaching note */}
          {qaData.coaching_note && (
            <div className="mt-3 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
              <p className="text-[10px] font-semibold text-indigo-700 uppercase mb-1">Coaching</p>
              <p className="text-xs text-indigo-800 leading-relaxed">{qaData.coaching_note}</p>
            </div>
          )}
        </div>
      )}

      {/* Transcript */}
      {(transcriptMessages.length > 0 || detail.transcript) && (
        <div className="card p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Transcript</h3>
            {/* Labelled "Regenerate" like the Sentiment and QA buttons above,
                but this one calls connector_service, so it needs
                analytics:connectors — not analytics:reports. */}
            {detail.processing_status === 'completed' && (
              <PermissionGate permission={StandardPermission.Analytics.CONNECTORS}>
              <button
                onClick={handleRegenerateTranscript}
                disabled={regeneratingTranscript}
                className="flex items-center space-x-1 text-[11px] text-gray-500 hover:text-primary-600 transition-colors disabled:opacity-50"
              >
                {regeneratingTranscript ? <Loader2 size={10} className="animate-spin" /> : <RotateCw size={10} />}
                <span>Regenerate</span>
              </button>
              </PermissionGate>
            )}
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {transcriptMessages.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {transcriptMessages.map((msg, i) => {
                  const isAgent = (msg.speaker || '').toLowerCase().includes('agent')
                    || (msg.speaker || '').toLowerCase().includes('bot');
                  const prevSpeaker = i > 0 ? transcriptMessages[i - 1].speaker : null;
                  const sameSpeaker = msg.speaker === prevSpeaker;
                  return (
                    <div key={i} className={`flex items-start gap-3 px-3 py-1.5 ${isAgent ? 'bg-primary-50/40' : ''}`}>
                      <span className="text-[10px] text-gray-400 font-mono w-12 flex-shrink-0 pt-0.5 text-right">
                        {msg.timestamp ? `${msg.timestamp}s` : ''}
                      </span>
                      <span className={`text-[11px] font-semibold w-16 flex-shrink-0 pt-0.5 ${isAgent ? 'text-primary-600' : 'text-gray-500'}`}>
                        {sameSpeaker ? '' : (msg.speaker || (isAgent ? 'Agent' : 'Customer'))}
                      </span>
                      <div className="flex items-start gap-1.5 min-w-0 flex-1">
                        {msg.sentiment && (
                          <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                            msg.sentiment === 'positive' ? 'bg-green-500'
                            : msg.sentiment === 'negative' ? 'bg-red-500'
                            : 'bg-gray-300'
                          }`} />
                        )}
                        <p className="text-sm text-gray-700 leading-snug">{msg.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{detail.transcript}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recording */}
      {detail.audio_file_path && audioUrl && (
        <div className="card p-5 mb-6">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Recording</h3>
          <audio controls className="w-full" preload="none" src={audioUrl} aria-label="Interaction recording" />
        </div>
      )}
    </div>
  );
};

export default ConnectorInteractionDetailPage;
